'use client';

import Link from 'next/link';
import useAdminGuard from '@/hooks/useAdminGuard';
import { Box, Stack, Typography } from '@/primitives';
import { Card, CardMedia, styled } from '@mui/material';
import {
  APP_ROUTE_NAMES,
  APP_ROUTE_PATHS,
  PUBLIC_ROUTES,
} from '@/configs/routes';
import { blue, neutral } from '@/lib/colours';

const cards = [
  {
    image: '/images/1f910133-2e95-4ef2-82ac-a1178f991bd1.png',
    headline: APP_ROUTE_NAMES.SEARCH,
    tagline:
      'The search hub for breeders, buyers and agents to find animals, members and more.',
  },
  {
    image: '/images/5974cdd2-d056-43f0-a637-bec0daf87a6b.png',
    headline: APP_ROUTE_NAMES.REGISTRY,
    tagline:
      'The management portal for breed registrars and staff to manage animals, members and more.',
  },
];

const LinkStyled = styled(Link)({
  minHeight: '100%',
  textDecoration: 'none',
});

export default function HeroSection() {
  const { handleClickLink } = useAdminGuard(APP_ROUTE_PATHS.DASHBOARD);

  return (
    <Box
      sx={{
        padding: { xs: '20px', md: '80px' },
        width: '100%',
        background: blue[900],
      }}
    >
      <Box sx={{ margin: 'auto', maxWidth: '1216px' }}>
        <Typography
          color="white"
          component="h1"
          sx={{
            fontSize: { xs: '20px', md: '40px' },
            lineHeight: { xs: '28px', md: '48px' },
            fontWeight: { xs: 400, md: 300 },
            textAlign: 'center',
          }}
        >
          Welcome to the BreedLOGIC Portal: Search. Discover. Decide.
        </Typography>
        <Typography
          color="white"
          component="p"
          sx={{
            mt: '12px',
            fontSize: { xs: '12px', md: '20px' },
            lineHeight: { xs: '20px', md: '28px' },
            textAlign: 'center',
          }}
        >
          Your platform for livestock breeding: search animal records, explore
          member profiles, browse sale catalogues, and access advanced breeding
          tools — all in one seamless experience.
        </Typography>
        <Stack
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            gap: { xs: '20px', md: '32px' },
            mt: { xs: '20px', md: '60px' },
            mb: { xs: '0px', md: '60px' },
          }}
        >
          {cards.map(({ headline, image, tagline }) => (
            <LinkStyled
              href={
                headline === APP_ROUTE_NAMES.SEARCH
                  ? PUBLIC_ROUTES.SEARCH
                  : APP_ROUTE_PATHS.DASHBOARD
              }
              key={headline}
              sx={{ flex: { sm: 1, md: 0 } }}
              onClick={
                headline === APP_ROUTE_NAMES.SEARCH
                  ? undefined
                  : handleClickLink
              }
            >
              <Card
                elevation={0}
                sx={{
                  padding: { xs: '12px', md: '24px' },
                  borderRadius: '8px',
                  height: '100%',
                  width: { xs: '100%', md: '384px' },
                }}
              >
                <CardMedia
                  alt={headline}
                  component="img"
                  image={image}
                  sx={{
                    width: '100%',
                    aspectRatio: { xs: '326 / 168', md: '336 / 260' },
                  }}
                />
                <Typography
                  color={neutral[900]}
                  sx={{
                    mt: { xs: '12px', md: '40px' },
                    fontSize: { xs: '14px', md: '20px' },
                    fontWeight: 500,
                    lineHeight: { xs: '20px', md: '28px' },
                  }}
                >
                  {headline}
                </Typography>
                <Typography
                  color={neutral[700]}
                  sx={{
                    mt: '12px',
                    fontSize: { xs: '12px', md: '16px' },
                    lineHeight: { xs: '20px', md: '24px' },
                  }}
                >
                  {tagline}
                </Typography>
              </Card>
            </LinkStyled>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
